import { AddressField } from '../model/address-field.model';
import { CustomerAddress } from '../model/customer-information.model';

/**
 * Country, city and postal code are mandatory (as in Retail CRM and ESO CRM):
 * - the private address must always carry them;
 * - the business address is optional, but must carry them as soon as it is set.
 *
 * An address counts as "entered" if any field other than country and province carries a value.
 * An address with only a country counts as not entered: Retail CRM cannot remove a stored country
 * (an empty value is replaced by the market default), and the province is derived by Tolerant Post.
 *
 * The backend mirrors this rule in MandatoryAddressCheck.
 */
const IGNORED_FOR_IS_SET: readonly (keyof CustomerAddress)[] = ['country', 'province'];

const isBlank = (value: string | undefined): boolean => !value || value.trim() === '';

export function isAddressSet(address: CustomerAddress | undefined): boolean {
  if (!address) {
    return false;
  }
  return Object.entries(address).some(
    ([key, value]) =>
      !IGNORED_FOR_IS_SET.includes(key as keyof CustomerAddress) && typeof value === 'string' && value.trim() !== '',
  );
}

function getMissingFields(address: CustomerAddress | undefined): AddressField[] {
  const missing: AddressField[] = [];
  if (isBlank(address?.country)) missing.push(AddressField.COUNTRY);
  if (isBlank(address?.city)) missing.push(AddressField.CITY);
  if (isBlank(address?.postalCode)) missing.push(AddressField.POSTAL_CODE);
  return missing;
}

/** The private address is always mandatory. */
export function getMissingPrivateAddressFields(address: CustomerAddress | undefined): AddressField[] {
  return getMissingFields(address);
}

/** The business address is optional, but complete as soon as it is set. */
export function getMissingBusinessAddressFields(address: CustomerAddress | undefined): AddressField[] {
  return isAddressSet(address) ? getMissingFields(address) : [];
}

export function areAddressesComplete(
  privateAddress: CustomerAddress | undefined,
  businessAddress: CustomerAddress | undefined,
): boolean {
  return (
    getMissingPrivateAddressFields(privateAddress).length === 0 &&
    getMissingBusinessAddressFields(businessAddress).length === 0
  );
}
